import { z } from "zod";

/**
 * Nafath Owner Identity shared contract (Strangler-Fig migration, manifest-6,
 * nafathVerifications slice).
 *
 * Single source of truth for the `NafathOwnerIdentity` wire shape shared by
 * the web SPA (`src/repositories/nafathVerifications.ts`) and the NestJS slice
 * (`apps/api/src/nafath/nafath-owner-identity.repository.ts`).
 *
 * ── What this is ─────────────────────────────────────────────────────────────
 * The staff "Owner Nafath Identity" modal reads the most-recent COMPLETED
 * verification row for an owner identified by their national_id (+optional
 * user_id). This shape is a STRICT SUBSET of the full `nafath_verifications`
 * row — it intentionally omits all internal/operational columns:
 *   OMITTED: oidc_state, trans_id, random_code, request_payload,
 *             response_payload, request_id, status, created_at
 *
 * ── Wire rules ────────────────────────────────────────────────────────────────
 * - All fields snake_case — direct DB field names, no renames.
 * - Date columns (dob_gregorian, id_expiry_gregorian, id_issue_gregorian,
 *   iqama_expiry_gregorian) are serialized as date-only strings "YYYY-MM-DD"
 *   (`.toISOString().split('T')[0]`) from the NestJS side. The Prisma schema
 *   stores them as plain String? so they arrive as strings from Prisma already.
 *   Hijri date variants (dob_hijri, etc.) are plain VARCHAR strings.
 * - `updated_at` is a full ISO timestamp (Timestamptz) serialized as-is.
 * - `id_type` is an integer (default 1 = Saudi NID; 2 = Iqama).
 *
 * ── Tolerance ─────────────────────────────────────────────────────────────────
 * `.passthrough()` lets the server add columns (new migrations) without
 * requiring a same-PR schema bump.
 *
 * BE adoption note: `apps/api` does not yet import this package at runtime —
 * it keeps its own typed wire interface (already shape-aligned). Wiring Nest to
 * consume `@dallali/shared` needs the dual ESM/CJS build step tracked in
 * `docs/migration/domain-debugging-matrix.md`.
 */

/**
 * The identity subset of `nafath_verifications` surfaced by the guarded
 * `GET /nafath/owner-identity` endpoint.
 *
 * The web SPA `.parse()`s Nest responses against this schema at the flip
 * boundary so any backend/frontend drift surfaces at runtime (and the repo
 * falls back to the Supabase path transparently).
 */
export const nafathOwnerIdentitySchema = z
  .object({
    id: z.string(),
    national_id: z.string(),
    id_type: z.number().int(), // NOT NULL DEFAULT 1 in the DB; server always sends it
    // Arabic name components (nullable — may be absent for Iqama holders).
    arabic_first_name: z.string().nullable().optional(),
    arabic_father_name: z.string().nullable().optional(),
    arabic_grand_father_name: z.string().nullable().optional(),
    arabic_family_name: z.string().nullable().optional(),
    // English name components (nullable — may be absent for Saudi NID holders).
    english_first_name: z.string().nullable().optional(),
    english_father_name: z.string().nullable().optional(),
    english_grand_father_name: z.string().nullable().optional(),
    english_family_name: z.string().nullable().optional(),
    // Demographics.
    gender: z.string().nullable().optional(),
    // Dates — Hijri strings (VARCHAR), Gregorian as date-only "YYYY-MM-DD".
    dob_hijri: z.string().nullable().optional(),
    dob_gregorian: z.string().nullable().optional(),
    // Nationality.
    nationality_ar: z.string().nullable().optional(),
    nationality_en: z.string().nullable().optional(),
    nationality_code: z.string().nullable().optional(),
    // ID expiry / issue.
    id_expiry_hijri: z.string().nullable().optional(),
    id_expiry_gregorian: z.string().nullable().optional(),
    id_issue_hijri: z.string().nullable().optional(),
    id_issue_gregorian: z.string().nullable().optional(),
    id_issue_location_ar: z.string().nullable().optional(),
    id_issue_location_en: z.string().nullable().optional(),
    // Iqama expiry (only for Iqama holders; null for Saudi NIDs).
    iqama_expiry_hijri: z.string().nullable().optional(),
    iqama_expiry_gregorian: z.string().nullable().optional(),
    // Audit timestamp.
    updated_at: z.string(),
  })
  // Tolerate extra columns the server may add without forcing a schema bump.
  .passthrough();

export type NafathOwnerIdentityDto = z.infer<typeof nafathOwnerIdentitySchema>;
